import { supabase } from '../../config/supabase';

export interface PerDiem {
  id: string;
  trip_id: string;
  country: string;
  days: number;
  days_with_accommodation?: number;
  days_without_accommodation?: number;
  daily_rate_usd: number;
  total_usd: number;
  exchange_rate_usd_to_ils: number | null;
  total_ils: number | null;
  calculation_date: string | null;
  manually_edited?: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreatePerDiemInput {
  trip_id: string;
  country: string;
  days: number;
  days_with_accommodation?: number;
  days_without_accommodation?: number;
  daily_rate_usd: number;
  total_usd: number;
  exchange_rate_usd_to_ils?: number;
  total_ils?: number;
  calculation_date?: string;
}

export interface UpdatePerDiemInput {
  days?: number;
  days_with_accommodation?: number;
  days_without_accommodation?: number;
  daily_rate_usd?: number;
  total_usd?: number;
  exchange_rate_usd_to_ils?: number;
  total_ils?: number;
  calculation_date?: string;
  manually_edited?: boolean;
}

class PerDiemService {
  /**
   * Get all per diems for a trip
   */
  async getByTripId(tripId: string): Promise<PerDiem[]> {
    const { data, error } = await supabase
      .from('per_diem' as any)
      .select('*')
      .eq('trip_id', tripId)
      .order('country', { ascending: true });

    if (error) {
      console.error('Error fetching per diems:', error);
      throw error;
    }

    return (data || []) as unknown as PerDiem[];
  }

  /**
   * Get a specific per diem by ID
   */
  async getById(id: string): Promise<PerDiem | null> {
    const { data, error } = await supabase
      .from('per_diem' as any)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching per diem:', error);
      throw error;
    }

    return data as unknown as PerDiem;
  }

  /**
   * Get per diem for a specific country in a trip
   */
  async getByTripAndCountry(tripId: string, country: string): Promise<PerDiem | null> {
    const { data, error } = await supabase
      .from('per_diem' as any)
      .select('*')
      .eq('trip_id', tripId)
      .eq('country', country)
      .single();

    if (error) {
      // Return null if not found (404 is expected)
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching per diem:', error);
      throw error;
    }

    return data as unknown as PerDiem;
  }

  /**
   * Create a new per diem entry
   */
  async create(input: CreatePerDiemInput): Promise<PerDiem> {
    const { data, error } = await supabase
      .from('per_diem' as any)
      .insert([input])
      .select()
      .single();

    if (error) {
      console.error('Error creating per diem:', error);
      throw error;
    }

    return data as unknown as PerDiem;
  }

  /**
   * Update an existing per diem entry
   */
  async update(id: string, input: UpdatePerDiemInput): Promise<PerDiem> {
    const { data, error} = await supabase
      .from('per_diem' as any)
      .update(input)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating per diem:', error);
      throw error;
    }

    return data as unknown as PerDiem;
  }

  /**
   * Upsert per diem (create if doesn't exist, update if exists)
   * Uses trip_id and country as unique constraint
   */
  async upsert(input: CreatePerDiemInput): Promise<PerDiem> {
    const { data, error } = await supabase
      .from('per_diem' as any)
      .upsert([input], {
        onConflict: 'trip_id,country',
        ignoreDuplicates: false,
      })
      .select()
      .single();

    if (error) {
      console.error('Error upserting per diem:', error);
      throw error;
    }

    return data as unknown as PerDiem;
  }

  /**
   * Delete a per diem entry
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('per_diem' as any)
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting per diem:', error);
      throw error;
    }
  }

  /**
   * Delete all per diems for a trip
   */
  async deleteByTripId(tripId: string): Promise<void> {
    const { error } = await supabase
      .from('per_diem' as any)
      .delete()
      .eq('trip_id', tripId);

    if (error) {
      console.error('Error deleting per diems:', error);
      throw error;
    }
  }

  /**
   * Batch upsert multiple per diems
   */
  async batchUpsert(inputs: CreatePerDiemInput[]): Promise<PerDiem[]> {
    const { data, error } = await supabase
      .from('per_diem' as any)
      .upsert(inputs, {
        onConflict: 'trip_id,country',
        ignoreDuplicates: false,
      })
      .select();

    if (error) {
      console.error('Error batch upserting per diems:', error);
      throw error;
    }

    return (data || []) as unknown as PerDiem[];
  }
}

export const perDiemService = new PerDiemService();
